import React, { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { FORM_LIST_LIMIT, useGetInspectionFormsQuery } from '../../../../../redux/rtkQuery/apiFormbuilderSlice';
import { dispatch } from '../../../../../redux/store';
import { setSelectedForm } from '../../../../../redux/slices/thresholdFormSlice';
import { RootState } from '../../../../../redux/root';
import { setPrevInspectionType, setSelectedInspection } from '../../../../../redux/slices/inspectionsSlice';
import useFormMarkupTools from '../../../../../organisms/ThresholdReports/useFormMarkupTools';
import { clearTemplateTexts } from '../../../../../organisms/ThresholdReports/docOvelayTool';

// `onFormAvailable` tells the editor whether this inspection has a builder form
// (threshold / specialty / jurisdiction). The form is no longer a tab of its
// own: the editor renders it under the details on the same page.
const InspectionTabs = ({ value, setValue, watch, onFormAvailable }) => {
  const inspectionType = watch('inspectionType');
  // Must match InspectionType.tsx's args exactly. RTK Query caches per
  // serialized argument, so `{limit: 10000}` and `{limit: 1000}` are two
  // separate cache entries — and both components mount in this editor, so the
  // multi-MB form list was being downloaded twice on every open.
  const { data: forms } = useGetInspectionFormsQuery({ limit: FORM_LIST_LIMIT });
  const [showForm, setShowForm] = useState(false);
  useEffect(() => {
    onFormAvailable(showForm);
  }, [showForm]);
  const { selectedInspection, prevInspectionType } = useSelector((s: RootState) => s.inspections);
  const { updateFormMarkupOnInspection } = useFormMarkupTools();

  useEffect(() => {
    return () => {
      dispatch(setPrevInspectionType(-1));
    };
  }, []);
  useEffect(() => {
    const inspectionTypeId = inspectionType?.id || inspectionType;
    if (forms && inspectionTypeId && inspectionTypeId !== prevInspectionType) {
      dispatch(setPrevInspectionType(inspectionTypeId));
      let showInspectionNotes = false;
      let formList = [];
      let listId;
      let formType = '';
      const formArray = [];
      forms.results.forEach(form => {
        if (form.jurisdictions.length > 0) {
          // process a Jurisdiction case
          formList = form.jurisdictions;
          listId = Number(selectedInspection.jurisdictionId);
          formType = 'Jurisdiction';
        } else {
          // process inpectionType case
          formList = form.inspectionTypes;
          listId = inspectionType.id || inspectionType;
          formType = 'inspectionType';
        }
        if (formList.indexOf(listId) > -1) {
          showInspectionNotes = true;
          formArray.push({ formType, form });
        }
      });

      let formData;
      // case that we have both Jurisdiction and inspectionType
      if (formArray.length > 1) {
        formData = formArray.find(f => f.formType === 'inspectionType');
      }

      if (formArray.length === 1) {
        formData = formArray[0];
      }

      if (formData) {
        dispatch(setSelectedForm(formData.form));
        const dataTemplate = clearTemplateTexts(formData.form.formMarkup, selectedInspection);
        dispatch(setSelectedInspection({ ...selectedInspection, formMarkup: dataTemplate }));
        // Persist ONLY when the inspection has no markup of its own yet — that
        // is the case this was written for (the server still holds null).
        //
        // It used to write on every open, which turned a stale read into real
        // data loss: the editor is handed a ROW from a cached inspection list,
        // so if that row's markup predated the last autosave, this line pushed
        // the old version back over the good one. When the inspection already
        // has markup, the rebased copy lives in Redux and reaches the server
        // with the next autosave or Update.
        if (!selectedInspection.formMarkup) {
          updateFormMarkupOnInspection(dataTemplate);
        }
      }

      // Shows the form under the details
      setShowForm(showInspectionNotes);
    }
  }, [inspectionType, forms]);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <Wrapper>
      <div>
        <Tabs>
          <Tab value={value} onClick={e => setValue(0)} index={0}>
            Details
          </Tab>
          <Tab value={value} onClick={e => setValue(1)} index={1}>
            Comments
          </Tab>
        </Tabs>
      </div>
    </Wrapper>
  );
};

export default InspectionTabs;

const Wrapper = styled.div`
  margin: 20px 24px 0;
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const Tab = styled.div<{ index: number; value: number }>`
  flex: 1;
  min-width: 120px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.15s ease, color 0.15s ease;
  background: ${({ index, value }) => (index === value ? 'var(--primary-600)' : 'transparent')};
  color: ${({ index, value }) => (index === value ? 'var(--neutral-0)' : 'var(--neutral-600)')};

  &:hover {
    background: ${({ index, value }) => (index === value ? 'var(--primary-700)' : 'var(--neutral-200)')};
    color: ${({ index, value }) => (index === value ? 'var(--neutral-0)' : 'var(--neutral-800)')};
  }
`;
